import React from 'react';
import { Search, Filter, Camera } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarcodeScanner } from './BarcodeScanner';
interface SearchBarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  searchFilter: 'all' | 'name' | 'barcode';
  onFilterChange: (filter: 'all' | 'name' | 'barcode') => void;
  onClearSearch: () => void;
  showNoResults?: boolean;
  onBarcodeDetected?: (barcode: string) => void;
  showBarcodeScanner?: boolean;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
}
export const SearchBar: React.FC<SearchBarProps> = ({
  searchQuery,
  onSearchChange,
  searchFilter,
  onFilterChange,
  onClearSearch,
  showNoResults = false,
  onBarcodeDetected,
  showBarcodeScanner = true,
  className,
  disabled = false,
  placeholder = "Rechercher un produit ou saisir code-barres..."
}) => {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onSearchChange(value);
  };
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onBarcodeDetected && searchQuery.trim()) {
      // Déclenchement manuel via Entrée
      onBarcodeDetected(searchQuery.trim());
    }
  };

  // Détection automatique avec debounce pour les codes-barres
  React.useEffect(() => {
    if (onBarcodeDetected && searchQuery.trim()) {
      // Debounce de 400ms pour éviter les appels excessifs
      const timer = setTimeout(() => {
        const trimmed = searchQuery.trim();
        // Détecter les codes-barres (8-13 chiffres) ou recherche de texte
        if (/^\d{8,13}$/.test(trimmed) || trimmed.length >= 3) {
          onBarcodeDetected(trimmed);
        }
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [searchQuery, onBarcodeDetected]);
  const handleBarcodeScanned = (barcode: string) => {
    onSearchChange(barcode);
    if (onBarcodeDetected) {
      onBarcodeDetected(barcode);
    }
  };

  return <div className={`space-y-4 ${className ?? ''}`}>
      <div className="flex flex-col sm:flex-row gap-4 items-center">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder={placeholder} 
            value={searchQuery} 
            onChange={handleInputChange} 
            onKeyPress={handleKeyPress} 
            className="pl-10 w-full" 
            disabled={disabled}
          />
        </div>
        
        {showBarcodeScanner && onBarcodeDetected && (
          <BarcodeScanner
            onBarcodeScanned={handleBarcodeScanned}
            autoStart
            trigger={
              <Button variant="outline" size="sm" className="shrink-0">
                <Camera className="w-4 h-4 mr-2" />
                Scanner
              </Button>
            }
          />
        )}

        {searchQuery && <Button variant="outline" size="sm" onClick={onClearSearch} className="shrink-0">
            Effacer
          </Button>}
      </div>
      
      {showNoResults && searchQuery && <div className="text-center py-4">
          <p className="text-muted-foreground">Aucun produit trouvé pour "{searchQuery}"</p>
        </div>}
    </div>;
};